"use client";

import { motion } from "framer-motion";
import { scaleInHover, fadeInUp } from "../../lib/animations";

interface TagProps {
  icon?: React.ReactNode;
  text: string;
  bgColor?: string;
  textColor?: string;
  borderColor?: string;
  className?: string;
  animated?: boolean;
}

export default function Tag({
  icon,
  text,
  bgColor = "light",
  textColor = "secondary",
  borderColor = "secondary",
  className = "",
  animated = true,
}: TagProps) {
  const baseClasses =
    "inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm font-medium shadow-sm";

  const getBgClass = (color: string) => {
    switch (color) {
      case "light":
        return "bg-light";
      case "primary":
        return "bg-primary";
      case "secondary":
        return "bg-secondary";
      default:
        return "bg-light";
    }
  };

  const getTextClass = (color: string) => {
    switch (color) {
      case "primary":
        return "text-primary";
      case "secondary":
        return "text-secondary";
      case "dark":
        return "text-dark";
      case "foreground":
        return "text-foreground";
      default:
        return "text-secondary";
    }
  };

  const getBorderClass = (color: string) => {
    switch (color) {
      case "primary":
        return "border-primary";
      case "secondary":
        return "border-secondary";
      case "border":
        return "border-border";
      default:
        return "border-secondary";
    }
  };

  const tagClasses = `${baseClasses} ${getBgClass(bgColor)} ${getTextClass(
    textColor
  )} ${getBorderClass(borderColor)} ${className}`;

  if (animated) {
    return (
      <motion.div
        className={tagClasses}
        variants={fadeInUp}
        whileHover={{ scale: 1.05, y: -2 }}
        whileTap={{ scale: 0.95 }}
        transition={{ duration: 0.2 }}
      >
        {icon && (
          <motion.span
            className="flex-shrink-0"
            whileHover={{ rotate: 10 }}
            transition={{ duration: 0.2 }}
          >
            {icon}
          </motion.span>
        )}
        <span>{text}</span>
      </motion.div>
    );
  }

  return (
    <div className={tagClasses}>
      {icon && <span className="flex-shrink-0">{icon}</span>}
      <span>{text}</span>
    </div>
  );
}
